// src/components/child/NewChartKPI.jsx
import React, { useEffect, useRef, useMemo, useState, useCallback } from "react";
import * as Chart from "chart.js";
import moment from "moment-hijri";
import HeadingComponent from "../BoardPack/components/shared/heading";

const { Chart: ChartJS, registerables } = Chart;
ChartJS.register(...registerables);

function groupDataByYear(data) {
  const grouped = {};
  data.forEach((item) => {
    if (!grouped[item.year]) grouped[item.year] = [];
    grouped[item.year].push(item);
  });
  return grouped;
}

const NewChartKPI = ({
  chartData = [],
  title = "",
  subtitle = "",
  chartTitle = "",
  colors: colorsProp = {},
  labels: labelsProp = {},
  showDataLabels = true,
  showRamadanRow = false,
  ramadanBorderColor = "#ffd700",
  yAxisFormatters = {},
  yAxisLabel = "On Street Parking",
  y1AxisLabel = "Occupancy %",
  bottomLabel = "Months",
  showRightAxisValues = true,
  topRightContent = null,
}) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const finalLabelRects = useRef([]);
  const containerRef = useRef(null);
  const cardRef = useRef(null);
  // Chart.js reserves left/right margin width for the y-axis tick labels
  // based on their text (e.g. "159,637" vs "21,635", right axis shown or
  // hidden) — this varies with the data, so the table's edge columns are
  // kept in sync with the chart's actual measured insets instead of a fixed
  // guess, to stay pixel-aligned with the bars above them.
  const [plotLeftInset, setPlotLeftInset] = useState(126);
  const [plotRightInset, setPlotRightInset] = useState(0);
  // One uniform font size for every data cell — sized to fit the single
  // widest value in the table, so every cell (biggest and smallest value
  // alike) renders at the same size.
  const [cellFontSize, setCellFontSize] = useState(10);


  // colorsProp/labelsProp/yAxisFormatters arrive as fresh object literals on
  // every parent render (even when their actual values are unchanged). These
  // are memoized on the underlying primitives/function refs, not the wrapper
  // object, since they sit in the chart-creation effect's dependency array —
  // a new reference there destroys and rebuilds the whole chart every render.
  const colors = useMemo(() => ({
    revenue: colorsProp?.revenue ?? "#4C96D9",
    transaction: colorsProp?.transaction ?? "#F08A3C",
    occupancy: colorsProp?.occupancy ?? colorsProp?.als ?? "#E8C33D",
  }), [colorsProp?.revenue, colorsProp?.transaction, colorsProp?.occupancy, colorsProp?.als]);

  const dataLabels = useMemo(() => ({
    revenue: labelsProp?.revenue ?? "Monthly Avg Daily Revenue",
    transaction: labelsProp?.transaction ?? labelsProp?.atv ?? "Monthly Avg Daily Transaction",
    occupancy: labelsProp?.occupancy ?? labelsProp?.als ?? "Parking Occupancy",
  }), [labelsProp?.revenue, labelsProp?.transaction, labelsProp?.atv, labelsProp?.occupancy, labelsProp?.als]);

  const defaultLeftFormatter = useCallback((v) => v.toLocaleString(), []);
  const defaultRightFormatter = useCallback((v) => `${v}%`, []);
  const leftFormatter = yAxisFormatters?.left ?? defaultLeftFormatter;
  const rightFormatter = yAxisFormatters?.right ?? defaultRightFormatter;

  const themeColors = {
    textColor: "#e5e7eb",
    gridColor: "rgba(255, 255, 255, 0.1)",
    borderColor: "#4b5563",
    backgroundColor: "#6B6A6C",
    labelBoxBg: "rgba(0, 0, 0, 0.4)",
    labelTextColor: "#ffffff",
    tooltipBg: "rgba(31, 41, 55, 0.95)",
    tooltipBorder: "#4b5563",
  };

  const axisRanges = useMemo(() => {
    if (!chartData.length) {
      return { y: { min: 0, max: 100 }, y1: { min: -5, max: 50 } };
    }

    const pad = (vals, factor) => {
      const filtered = vals.filter((v) => v != null && !isNaN(v));
      if (!filtered.length) return { min: 0, max: 100 };
      const mn = Math.min(...filtered);
      const mx = Math.max(...filtered);
      const range = mx - mn || Math.abs(mx) * 0.2 || 1;
      return {
        min: Math.max(0, Math.floor(mn - range * factor)),
        max: Math.ceil(mx + range * factor),
      };
    };

    // When the right axis isn't a real percentage (showRightAxisValues=false,
    // e.g. breakdown mode where the 3rd series is just another count), the
    // 3rd series is drawn on the primary "y" axis instead of "y1" — so it
    // needs to be included in the same padding calc as revenue/transaction.
    const yVals = [
      ...chartData.map((d) => d.revenue),
      ...chartData.map((d) => d.transaction),
    ];
    if (!showRightAxisValues) yVals.push(...chartData.map((d) => d.occupancy));
    const yRange = pad(yVals, 0.25);

    const occVals = chartData.map((d) => d.occupancy).filter((v) => v != null);
    const occMax = occVals.length ? Math.max(...occVals) : 50;
    // Dynamic: round up to the next 50 above the actual max (94.01% → 100%,
    // 120% → 150%), instead of a fixed occMax/0.3 blow-up that left the real
    // data compressed into a small sliver of the axis.
    const y1Max = Math.max(50, Math.ceil(occMax / 50) * 50);
    // Flush with 0, same as the left "y" axis — no reserved negative buffer,
    // so the 0% gridline sits right at the bars' base instead of floating
    // above it.
    const y1Min = 0;

    return { y: yRange, y1: { min: y1Min, max: y1Max } };
  }, [chartData, showRightAxisValues]);

  const getRamadanMonths = useMemo(() => {
    if (!showRamadanRow || !chartData.length) return [];

    const monthMap = {
      Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
      Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11,
    };

    const ramadanData = [];
    chartData.forEach((item, idx) => {
      const monthName = item.month.split("-")[0];
      const year = parseInt(item.year);
      const monthNum = monthMap[monthName] !== undefined ? monthMap[monthName] : 0;

      const hijriYear = year - 579;
      const ramadanFirstDay = moment(`${hijriYear}-9-1`, "iYYYY-iM-iD");
      const ramadanStartGregMonth = ramadanFirstDay.month();
      const ramadanStartGregYear = ramadanFirstDay.year();
      const isRamadan = ramadanStartGregYear === year && ramadanStartGregMonth === monthNum;

      if (isRamadan) ramadanData.push(idx);
    });

    return ramadanData;
  }, [chartData, showRamadanRow]);

  // Also memoized: these sit in the chart-creation effect's dependency array
  // below, so recreating them as fresh arrays on every render (even when
  // chartData itself hasn't changed) would destroy/rebuild the chart on
  // every re-render — same failure mode as colors/dataLabels above.
  const { months, yearRanges } = useMemo(() => {
    const grouped = groupDataByYear(chartData);
    return {
      months: chartData.map((d) => d.month.split("-")[0]),
      yearRanges: Object.keys(grouped).map((year) => ({
        year,
        span: grouped[year].length,
      })),
    };
  }, [chartData]);

  useEffect(() => {
    if (!chartRef.current || !chartData.length) return;
    if (chartInstance.current) chartInstance.current.destroy();

    const ctx = chartRef.current.getContext("2d");
    finalLabelRects.current = [];
    const ramadanMonthIndices = new Set(getRamadanMonths);

    // ─── HELPERS ────────────────────────────────────────────────────────────
    const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

    /**
     * Resolve vertical collisions for one group of labels.
     * `obstacles` = array of already-placed labels from other groups (immovable).
     * `direction` = preferred push direction if blocked by an obstacle ('up'|'down').
     */
    const resolveGroupCollisions = (labels, obstacles, chartTop, chartBottom, direction) => {
      const V_GAP = 3, H_GAP = 2;
      const overlaps = (a, b) => {
        if (Math.abs(a.x - b.x) > (a.w + b.w) / 2 + H_GAP) return false;
        if (Math.abs(a.fy - b.fy) > (a.h + b.h) / 2 + V_GAP) return false;
        return true;
      };

      for (let pass = 0; pass < 60; pass++) {
        let moved = false;

        // resolve within own group
        for (let i = 0; i < labels.length; i++) {
          for (let j = i + 1; j < labels.length; j++) {
            const a = labels[i], b = labels[j];
            if (!overlaps(a, b)) continue;
            moved = true;
            const shift = (a.h + b.h) / 2 + V_GAP - Math.abs(a.fy - b.fy);
            if (a.fy <= b.fy) {
              a.fy = clamp(a.fy - shift / 2, chartTop, chartBottom);
              b.fy = clamp(b.fy + shift / 2, chartTop, chartBottom);
            } else {
              b.fy = clamp(b.fy - shift / 2, chartTop, chartBottom);
              a.fy = clamp(a.fy + shift / 2, chartTop, chartBottom);
            }
          }
        }

        // push away from immovable obstacles
        for (const lbl of labels) {
          for (const obs of obstacles) {
            if (!overlaps(lbl, obs)) continue;
            moved = true;
            const shift = (lbl.h + obs.h) / 2 + V_GAP - Math.abs(lbl.fy - obs.fy);
            lbl.fy = clamp(
              lbl.fy + (direction === "up" ? -shift : shift),
              chartTop,
              chartBottom
            );
          }
        }

        if (!moved) break;
      }
    };

    const drawBox = (ctx, lbl) => {
      const bx = lbl.x - lbl.w / 2;
      const by = lbl.fy - lbl.h / 2;
      // No box background/border — just the value text in its series color.
      ctx.fillStyle = lbl.color;

      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      // Must match the font each group measured itself with when it computed
      // lbl.w (11px for revenue, 9px for transaction/occupancy) — this used
      // to always draw at 11px regardless, so transaction/occupancy text
      // rendered wider than the box the collision-avoidance pass had already
      // cleared for it, and would visually bleed into its neighbor whenever
      // points sat close together (many months / narrow chart).
      ctx.font = `bold ${lbl.fs}px Arial`;
      ctx.fillText(lbl.text, Math.round(lbl.x), Math.round(lbl.fy));

      // still tracked for the ramadan/collision plugins, which expect a rect
      finalLabelRects.current.push({
        left: bx - 4, right: bx + lbl.w + 4,
        top: by - 4, bottom: by + lbl.h + 4,
      });
    };
    // ────────────────────────────────────────────────────────────────────────

    // Custom legend plugin
    const customLegendPlugin = {
      id: "customLegend",
      afterDraw(chart) {
        const legend = chart.legend;
        if (!legend?.legendItems) return;
        const ctx = chart.ctx;
        ctx.save();
        legend.legendItems.forEach((item, i) => {
          const hitBox = legend.legendHitBoxes?.[i];
          if (!hitBox) return;
          const centerY = hitBox.top + hitBox.height / 2;
          const iconW = 22;
          const iconX = hitBox.left;
          ctx.clearRect(iconX, hitBox.top, iconW + 2, hitBox.height);

          if (i === 0) {
            ctx.fillStyle = colors.revenue;
            ctx.fillRect(iconX, centerY - 5, iconW, 10);
          } else if (i === 1) {
            ctx.strokeStyle = colors.transaction;
            ctx.lineWidth = 2.5;
            ctx.setLineDash([]);
            ctx.beginPath();
            ctx.moveTo(iconX, centerY);
            ctx.lineTo(iconX + iconW, centerY);
            ctx.stroke();
            ctx.fillStyle = colors.transaction;
            ctx.beginPath();
            ctx.arc(iconX + iconW / 2, centerY, 4, 0, Math.PI * 2);
            ctx.fill();
          } else if (i === 2) {
            ctx.strokeStyle = colors.occupancy;
            ctx.lineWidth = 2.5;
            ctx.setLineDash([]);
            ctx.beginPath();
            ctx.moveTo(iconX, centerY);
            ctx.lineTo(iconX + iconW, centerY);
            ctx.stroke();
          }
        });
        ctx.restore();
      },
    };

    // ─── UNIFIED LABEL PLUGIN (replaces both revenueLabelPlugin & lineLabelPlugin) ───
    const unifiedLabelPlugin = {
      id: "unifiedLabels",
      afterDatasetsDraw(chart) {
        if (!showDataLabels) return;

        finalLabelRects.current = []; // reset before re-drawing

        const ctx = chart.ctx;
        const yScale = chart.scales.y;
        const chartTop = yScale.top + 4;
        const chartBottom = yScale.bottom - 4;
        const PAD = 3;

        ctx.save();

        // ── 1. REVENUE labels (above bar tops) ──────────────────────────────
        const revMeta = chart.getDatasetMeta(0);
        const revLabels = [];

        if (revMeta && !revMeta.hidden) {
          ctx.font = "bold 11px Arial";
          revMeta.data.forEach((bar, i) => {
            const value = chart.data.datasets[0].data[i];
            if (value == null || value === 0) return;
            if (ramadanMonthIndices.has(i)) return;
            const text = leftFormatter(value);
            const tw = ctx.measureText(text).width;
            const w = tw + PAD * 2 + 8, h = 20;
            revLabels.push({
              x: bar.x,
              fy: clamp(bar.y - h - 8, chartTop, chartBottom),
              anchorY: bar.y,
              text, w, h,
              color: colors.revenue,
              bgColor: "#333333",
              fs: 11,
              lineWidth: 1.2,
            });
          });
        }

        resolveGroupCollisions(revLabels, [], chartTop, chartBottom, "up");

        // ── 2. TRANSACTION labels (above line points) ────────────────────────
        const txMeta = chart.getDatasetMeta(1);
        const txLabels = [];

        if (txMeta && !txMeta.hidden) {
          ctx.font = "bold 9px Arial";
          txMeta.data.forEach((pt, i) => {
            const value = chart.data.datasets[1].data[i];
            if (value == null || value === 0) return;
            // if (ramadanMonthIndices.has(i)) return;
            const text = Math.round(value).toLocaleString();
            const tw = ctx.measureText(text).width;
            const w = tw + PAD * 2 + 12, h = 18;
            txLabels.push({
              x: pt.x,
              fy: clamp(pt.y - h - 6, chartTop, chartBottom),
              anchorY: pt.y,
              text, w, h,
              color: colors.transaction,
              bgColor: "#464647",
              fs: 9,
              lineWidth: 1,
            });
          });
        }

        const revObs = revLabels.map((l) => ({ x: l.x, fy: l.fy, w: l.w, h: l.h }));
        resolveGroupCollisions(txLabels, revObs, chartTop, chartBottom, "up");

        const txObs = txLabels.map((l) => ({ x: l.x, fy: l.fy, w: l.w, h: l.h }));

        // ── 3. OCCUPANCY labels ────────────────────────────────────────────
        // On the real occupancy axis (y1, a %) the line usually sits low
        // relative to the bars/counts on y, so drawing its labels BELOW the
        // line keeps them clear of everything else. But when this series
        // shares the primary "y" axis instead (showRightAxisValues=false —
        // e.g. breakdown mode, where it's just another count), it can dip
        // down near the axis floor same as the bars, so "below" leaves no
        // room and labels pile up at the bottom edge. Draw above instead,
        // same side as the bar/1st line, in that case.
        const occMeta = chart.getDatasetMeta(2);
        const occLabels = [];
        const occDirection = showRightAxisValues ? "down" : "up";

        if (occMeta && !occMeta.hidden) {
          ctx.font = "bold 9px Arial";
          occMeta.data.forEach((pt, i) => {
            const value = chart.data.datasets[2].data[i];
            if (value == null || value === 0) return;
            // if (ramadanMonthIndices.has(i)) return;
            const text = rightFormatter(value);
            const tw = ctx.measureText(text).width;
            const w = tw + PAD * 2 + 12, h = 18;
            occLabels.push({
              x: pt.x,
              fy: clamp(occDirection === "down" ? pt.y + h + 6 : pt.y - h - 6, chartTop, chartBottom),
              anchorY: pt.y,
              text, w, h,
              color: colors.occupancy,
              textColor: colors.occupancy,   // ← yeh add karo
              bgColor: "#464647",
              fs: 9,
              lineWidth: 1,
            });
          });
        }

        resolveGroupCollisions(occLabels, [...revObs, ...txObs], chartTop, chartBottom, occDirection);

        // ── Draw all groups (back to front) ──────────────────────────────────
        occLabels.forEach((l) => drawBox(ctx, l));
        txLabels.forEach((l) => drawBox(ctx, l));
        revLabels.forEach((l) => drawBox(ctx, l));

        ctx.restore();
      },
    };

    // Keeps the table's first column width in sync with the chart's actual
    // measured left inset (varies with y-axis tick label width) instead of
    // a fixed guess, so bars stay aligned with their table columns.
    const layoutSyncPlugin = {
      id: "layoutSync",
      afterLayout(chart) {
        const left = Math.round(chart.chartArea.left);
        const right = Math.round(chart.width - chart.chartArea.right);
        setPlotLeftInset((prev) => (prev === left ? prev : left));
        setPlotRightInset((prev) => (prev === right ? prev : right));
      },
    };

    // Ramadan plugin (unchanged)
    const ramadanPlugin = (() => {
      const hasRamadan = showRamadanRow && getRamadanMonths.length > 0;
      if (!hasRamadan) return { id: "ramadanPlugin_disabled", afterDraw() {} };

      return {
        id: "ramadanPlugin",
        beforeDraw(chart) {
          const ctx = chart.ctx;
          const xAxis = chart.scales.x;
          const yAxis = chart.scales.y;

          const BOX_H = 24;
          const ARROW_W = 16;
          const H_PAD = 8;
          const V_PAD = 8;
          const BR = 5;
          const chartWidth = xAxis.right - xAxis.left;
          const perBarWidth = chartWidth / (chartData.length || 1);
          const BOX_W = Math.min(165, Math.max(110, perBarWidth * 3.2));

          const chartTop = yAxis.top;
          const chartBottom = yAxis.bottom;
          const chartLeft = xAxis.left;
          const chartRight = xAxis.right;

          const clampX = (x, anchorX) => {
            const maxShift = BOX_W * 0.4;
            const anchored = Math.max(
              anchorX - maxShift,
              Math.min(anchorX + maxShift - BOX_W, x)
            );
            return Math.max(
              chartLeft + 4,
              Math.min(chartRight - BOX_W - 4, anchored)
            );
          };
          const clampY = (y) =>
            Math.max(chartTop + 4, Math.min(chartBottom - BOX_H - 4, y));

          const mkRect = (l, t, w, h) => ({
            left: l, right: l + w, top: t, bottom: t + h,
          });

          const overlaps = (a, b, hPad = H_PAD, vPad = V_PAD) =>
            a.left - hPad < b.right + hPad &&
            a.right + hPad > b.left - hPad &&
            a.top - vPad < b.bottom + vPad &&
            a.bottom + vPad > b.top - vPad;

          const obstacles = [];

          finalLabelRects.current.forEach((r) =>
            obstacles.push({
              left: r.left - 3, right: r.right + 3,
              top: r.top - 2, bottom: r.bottom + 2,
            })
          );

          chart.data.datasets.forEach((dataset, dsIdx) => {
            if (dataset.type !== "bar") return;
            const meta = chart.getDatasetMeta(dsIdx);
            if (meta.hidden) return;
            meta.data.forEach((bar) => {
              if (!bar) return;
              try {
                const props = bar.getProps(["x", "y", "base", "width"], true);
                const bL = props.x - props.width / 2;
                const bR = props.x + props.width / 2;
                const bTop = Math.min(props.y, props.base);
                const bBot = Math.max(props.y, props.base);
                if (!isNaN(bL) && !isNaN(bTop)) {
                  obstacles.push({ left: bL - 3, right: bR + 3, top: bTop - 3, bottom: bBot + 3 });
                }
              } catch (_) {}
            });
          });

          chart.data.datasets.forEach((dataset, dsIdx) => {
            if (dataset.type === "bar") return;
            const meta = chart.getDatasetMeta(dsIdx);
            if (meta.hidden) return;
            meta.data.forEach((pt) => {
              if (!pt) return;
              const px = pt.x, py = pt.y;
              if (px == null || py == null || isNaN(px) || isNaN(py)) return;
              obstacles.push({ left: px - 8, right: px + 8, top: py - 8, bottom: py + 8 });
            });
          });

          const getBarTopY = (idx) => {
            const meta = chart.getDatasetMeta(0);
            if (meta?.data?.[idx]) {
              const bar = meta.data[idx];
              try {
                const props = bar.getProps(["y", "base"], true);
                return Math.min(props.y, props.base ?? props.y);
              } catch (_) {
                return Math.min(bar.y, bar.base ?? bar.y);
              }
            }
            return yAxis.getPixelForValue(0);
          };

          const items = [];
          let mi = 0;
          yearRanges.forEach((yr) => {
            for (let i = 0; i < yr.span; i++) {
              if (ramadanMonthIndices.has(mi)) {
                const val = chartData[mi]?.revenue ?? 0;
                const dataX = xAxis.getPixelForValue(mi);
                const dataY = getBarTopY(mi);
                items.push({
                  label: `RAMADHAN ${yr.year} ${months[mi]} : ${val.toLocaleString()}`,
                  dataX, dataY, revenue: val,
                });
              }
              mi++;
            }
          });

          if (!items.length) return;

          const vertGaps = [8, 20, 36, 55, 78, 105, 135, 170, 210, 255, 300, 350];
          const horizShifts = [0, -15, 15, -30, 30, -50, 50, -75, 75, -100, 100];
          const candidateOffsets = [];
          vertGaps.forEach((dy) =>
            horizShifts.forEach((dx) => candidateOffsets.push([dx, dy]))
          );

          const placedBoxes = [];

          items.forEach(({ label, dataX, dataY }) => {
            const arrowTipY = dataY;
            let chosenX = null, chosenY = null, placed = false;

            for (const [dx, dy] of candidateOffsets) {
              const candidateBoxX = clampX(dataX + dx - BOX_W / 2, dataX);
              const candidateBoxY = clampY(arrowTipY - dy - BOX_H);
              const testRect = mkRect(candidateBoxX, candidateBoxY, BOX_W, BOX_H);
              const allObs = [...obstacles, ...placedBoxes];
              if (!allObs.some((obs) => overlaps(testRect, obs))) {
                chosenX = candidateBoxX;
                chosenY = candidateBoxY;
                placed = true;
                break;
              }
            }

            if (!placed) {
              let bestScore = Infinity, bestX = clampX(dataX - BOX_W / 2, dataX), bestY = chartTop + 6;
              candidateOffsets.slice(0, 60).forEach(([dx, dy]) => {
                const bx = clampX(dataX + dx - BOX_W / 2, dataX);
                const by = clampY(arrowTipY - dy - BOX_H);
                const r = mkRect(bx, by, BOX_W, BOX_H);
                const allObs = [...obstacles, ...placedBoxes];
                const score = allObs.filter((obs) => overlaps(r, obs, 0, 0)).length;
                if (score < bestScore) { bestScore = score; bestX = bx; bestY = by; }
              });
              chosenX = bestX;
              chosenY = bestY;
            }

            const chosenRect = mkRect(chosenX, chosenY, BOX_W, BOX_H);
            placedBoxes.push(chosenRect);
            obstacles.push({
              left: chosenRect.left - H_PAD, right: chosenRect.right + H_PAD,
              top: chosenRect.top - V_PAD, bottom: chosenRect.bottom + V_PAD,
            });

            const bTop = chosenY, bBot = chosenY + BOX_H;
            const baseCX = Math.min(
              Math.max(dataX, chosenX + ARROW_W / 2 + BR),
              chosenX + BOX_W - ARROW_W / 2 - BR
            );
            const baseBL = baseCX - ARROW_W / 2;
            const baseBR = baseCX + ARROW_W / 2;

            const drawShape = () => {
              ctx.beginPath();
              ctx.moveTo(chosenX + BR, bTop);
              ctx.lineTo(chosenX + BOX_W - BR, bTop);
              ctx.arcTo(chosenX + BOX_W, bTop, chosenX + BOX_W, bTop + BR, BR);
              ctx.lineTo(chosenX + BOX_W, bBot);
              ctx.lineTo(baseBR, bBot);
              ctx.lineTo(dataX, arrowTipY);
              ctx.lineTo(baseBL, bBot);
              ctx.lineTo(chosenX, bBot);
              ctx.lineTo(chosenX, bTop + BR);
              ctx.arcTo(chosenX, bTop, chosenX + BR, bTop, BR);
              ctx.closePath();
            };

            ctx.save();
            ctx.shadowColor = colors.transaction + "99";
            ctx.shadowBlur = 12;
            ctx.strokeStyle = colors.transaction;
            ctx.lineWidth = 0.5;
            drawShape();
            ctx.stroke();
            ctx.shadowColor = "transparent";
            ctx.shadowBlur = 0;
            ctx.fillStyle = "transparent";
            drawShape();
            ctx.fill();
            ctx.strokeStyle = ramadanBorderColor;
            ctx.lineWidth = 1.4;
            drawShape();
            ctx.stroke();
            ctx.fillStyle = "#ffffff";
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.font = "bold 9.5px Arial";
            ctx.fillText(label, Math.round(chosenX + BOX_W / 2), Math.round(chosenY + BOX_H / 2));
            ctx.restore();
          });
        },
      };
    })();

    chartInstance.current = new ChartJS(ctx, {
      type: "bar",
      data: {
        labels: chartData.map((d) => d.month),
        datasets: [
          {
            type: "bar",
            label: dataLabels.revenue,
            data: chartData.map((d) => d.revenue),
            backgroundColor: "transparent",
            borderColor: colors.revenue,
            borderWidth: 1.5,
            maxBarThickness: 24,
            categoryPercentage: 1.0,
            barPercentage: 0.9,
            yAxisID: "y",
          },
          {
            type: "line",
            label: dataLabels.transaction,
            data: chartData.map((d) => d.transaction),
            borderColor: colors.transaction,
            backgroundColor: colors.transaction,
            borderWidth: 2,
            pointRadius: 0,
            pointBackgroundColor: colors.transaction,
            yAxisID: "y",
          },
          {
            type: "line",
            label: dataLabels.occupancy,
            data: chartData.map((d) => d.occupancy),
            borderColor: colors.occupancy,
            backgroundColor: "transparent",
            borderWidth: 2,
            pointRadius: 0,
            yAxisID: showRightAxisValues ? "y1" : "y",
          },
        ],
      },
      options: {
        devicePixelRatio: (typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1) * 2,
        responsive: false,
        maintainAspectRatio: false,
        interaction: { mode: null },
        hover: { mode: null },
        events: [],
        plugins: {
          legend: {
            display: true,
            position: "top",
            labels: {
              color: "#ffffff",
              usePointStyle: false,
              boxWidth: 24,
              boxHeight: 12,
              generateLabels: (chart) =>
                chart.data.datasets.map((ds, i) => ({
                  text: ds.label,
                  datasetIndex: i,
                  hidden: false,
                  fillStyle: "transparent",
                  strokeStyle: "transparent",
                  lineWidth: 0,
                  fontColor: "#ffffff",
                })),
            },
          },
          tooltip: { enabled: false },
        },
        scales: {
          x: {
            offset: true,
            grid: { display: false },
            ticks: { display: false },
          },
          y: {
            type: "linear",
            position: "left",
            min: axisRanges.y.min,
            max: axisRanges.y.max,
            ticks: {
              color: themeColors.textColor,
              font: { size: 12 },
              callback: leftFormatter,
            },
            grid: { display: false },
            // title: {
            //   display: true,
            //   text: yAxisLabel,
            //   color: themeColors.textColor,
            //   font: { size: 10, weight: "bold" },
            // },
          },
          y1: {
            type: "linear",
            position: "right",
            min: axisRanges.y1.min,
            max: axisRanges.y1.max,
            ticks: {
              display: showRightAxisValues,
              color: themeColors.textColor,
              font: { size: 12 },
              stepSize: 15,
              callback: (v) => (showRightAxisValues && v >= 0 ? rightFormatter(v) : ""),
            },
            grid: { display: false },
            // title: {
            //   display: showRightAxisValues,
            //   text: y1AxisLabel,
            //   color: themeColors.textColor,
            //   font: { size: 10, weight: "bold" },
            // },
          },
        },
        layout: {
          padding: { left: 45, right: 0, top: 30, },
        },
      },
      plugins: [
        customLegendPlugin,
        unifiedLabelPlugin,   // ← replaces revenueLabelPlugin + lineLabelPlugin
        ramadanPlugin,
        layoutSyncPlugin,
      ],
    });

    const resizeObserver = new ResizeObserver(() => {
  if (!containerRef.current || !chartInstance.current) return;
  const w = containerRef.current.offsetWidth;
  const h = containerRef.current.offsetHeight;
  chartInstance.current.resize(w, h);
});
if (containerRef.current) resizeObserver.observe(containerRef.current);

return () => {
  resizeObserver.disconnect();
  if (chartInstance.current) chartInstance.current.destroy();
};
  }, [
    chartData,
    axisRanges,
    showDataLabels,
    showRamadanRow,
    getRamadanMonths,
    months,
    yearRanges,
    colors,
    dataLabels,
    leftFormatter,
    rightFormatter,
    ramadanBorderColor,
    yAxisLabel,
    y1AxisLabel,
    showRightAxisValues,
  ]);

  // Table rendering
  const groupedData2 = groupDataByYear(chartData);
  const tableMonths = chartData.map((d) => d.month.split("-")[0]);
  const tableYearRanges = Object.keys(groupedData2).map((year) => ({
    year,
    span: groupedData2[year].length,
  }));

  const RevenueIndicator = () => (
    <span style={{ display: "inline-flex", alignItems: "center", marginRight: 4, verticalAlign: "middle" }}>
      <span style={{ display: "inline-block", width: 14, height: 8, backgroundColor: colors.revenue, borderRadius: 1 }} />
    </span>
  );
  const TransactionIndicator = () => (
    <span style={{ display: "inline-flex", alignItems: "center", marginRight: 4, verticalAlign: "middle", position: "relative", width: 20, height: 10 }}>
      <span style={{ position: "absolute", top: "50%", left: 0, right: 0, height: 2, backgroundColor: colors.transaction, transform: "translateY(-50%)" }} />
      <span style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: 6, height: 6, borderRadius: "50%", backgroundColor: colors.transaction }} />
    </span>
  );
  const OccupancyIndicator = () => (
    <span style={{ display: "inline-flex", alignItems: "center", marginRight: 4, verticalAlign: "middle" }}>
      <span style={{ display: "inline-block", width: 18, height: 2, backgroundColor: colors.occupancy }} />
    </span>
  );

  const safeLeft = (v) => (v == null ? "-" : leftFormatter(v));
  const safeRight = (v) => (v == null ? "-" : rightFormatter(v));
  const safeInt = (v) => (v == null ? "-" : Math.round(v).toLocaleString());

  // Data-cell font size: measure the widest formatted value against the
  // width actually available per month column (total card width minus the
  // chart's left/right insets, split across chartData.length columns) and
  // shrink the font until it fits, instead of a fixed 10px that overflows
  // its cell once enough months are packed in.
  useEffect(() => {
    if (!cardRef.current || !chartData.length) return;

    const measureCanvas = document.createElement("canvas");
    const mctx = measureCanvas.getContext("2d");
    const BASE_SIZE = 10;
    const MIN_SIZE = 5;
    const CELL_PAD = 4; // px-0.5 padding + border, both sides

    const recompute = () => {
      const totalWidth = cardRef.current?.offsetWidth ?? 0;
      const numCols = chartData.length;
      if (!totalWidth || !numCols) return;

      const perCol = Math.max(0, (totalWidth - plotLeftInset - plotRightInset) / numCols - CELL_PAD);

      mctx.font = `${BASE_SIZE}px Arial`;
      let widest = 0;
      chartData.forEach((d) => {
        widest = Math.max(
          widest,
          mctx.measureText(safeLeft(d.revenue)).width,
          mctx.measureText(safeInt(d.transaction)).width,
          mctx.measureText(safeRight(d.occupancy)).width
        );
      });

      const size = widest > perCol && widest > 0
        ? Math.max(MIN_SIZE, Math.floor(BASE_SIZE * (perCol / widest)))
        : BASE_SIZE;
      setCellFontSize((prev) => (prev === size ? prev : size));
    };

    recompute();
    const ro = new ResizeObserver(recompute);
    ro.observe(cardRef.current);
    return () => ro.disconnect();
  }, [chartData, plotLeftInset, plotRightInset, leftFormatter, rightFormatter]);

  return (
    <div className="w-full ">
      <div
        className="w-full mx-auto px-1 py-1 bg-card"
      >
      {/* Header Section */}
        <div
          className="text-center"
        >
          {title && (
            <HeadingComponent variant={"main"}>{title}</HeadingComponent>
          )}
          {subtitle && (
            <HeadingComponent variant={"sub"}>{subtitle}</HeadingComponent>
          )}
        </div>

      <div
        ref={cardRef}
        className="w-full p-0.5"
        style={{ backgroundColor: "#464647", borderRadius: "8px" }}
      >
        <div className="" >
          {chartTitle && (
            <h3 className="text-center text-xl font-semibold mb-0" style={{ color: "#ffffff" }}>
              {chartTitle}
            </h3>
          )}
          <div className=" h-[550px] relative" ref={containerRef}>

            <canvas ref={chartRef}></canvas>
            {topRightContent && (
              <div className="absolute top-2 right-2 z-10">{topRightContent}</div>
            )}
          </div>
        </div>

        <div className="w-full ">
          <div className="overflow-x-auto">
            <table className="w-full !border-collapse text-xs table-fixed">
              <thead>
                <tr>
                  <th className="py-0.5 px-1 text-center font-normal" style={{ width: `${plotLeftInset}px`, minWidth: `${plotLeftInset}px` }}></th>
                  {tableMonths.map((month, idx) => (
                    <th
                      key={idx}
                      className="!border !border-border px-0.5 py-0.5 text-center font-normal text-[10px] transition-colors hover:bg-white/15"
                      style={{ color: "#ffffff", borderColor: "#4b5563" }}
                    >
                      {month}
                    </th>
                  ))}
                  {plotRightInset > 0 && <th style={{ width: `${plotRightInset}px`, minWidth: `${plotRightInset}px` }}></th>}
                </tr>
                <tr>
                  <td className="py-0.5 px-1" style={{ width: `${plotLeftInset}px`, minWidth: `${plotLeftInset}px` }}></td>
                  {tableYearRanges.map((range, idx) => (
                    <td key={idx} colSpan={range.span} className="!border !border-border px-0.5 py-0.5 text-center text-[10px] font-bold" style={{ color: "#ffffff", borderColor: "#4b5563" }}>
                      {range.year}
                    </td>
                  ))}
                  {plotRightInset > 0 && <td style={{ width: `${plotRightInset}px`, minWidth: `${plotRightInset}px` }}></td>}
                </tr>
              </thead>
              <tbody>
                {[
                  { Icon: RevenueIndicator, label: dataLabels.revenue, getData: (d) => safeLeft(d.revenue) },
                  { Icon: TransactionIndicator, label: dataLabels.transaction, getData: (d) => safeInt(d.transaction) },
                  { Icon: OccupancyIndicator, label: dataLabels.occupancy, getData: (d) => safeRight(d.occupancy) },
                ].map((row, ri) => (
                  <tr key={ri}>
                    <td className="!border !border-border px-1 py-1.5 font-normal text-[8px] text-left whitespace-nowrap" style={{ width: `${plotLeftInset}px`, minWidth: `${plotLeftInset}px`, color: "#ffffff", borderColor: "#4b5563" }} title={row.label}>
                      <row.Icon />
                      {row.label}
                    </td>
                    {chartData.map((item, idx) => (
                      <td
                        key={idx}
                        className="!border !border-border px-0.5 py-1.5 text-center whitespace-nowrap overflow-hidden transition-colors hover:bg-white/15"
                        style={{ color: "#ffffff", borderColor: "#4b5563", fontSize: `${cellFontSize}px` }}
                        title={row.getData(item)}
                      >
                        {row.getData(item)}
                      </td>
                    ))}
                    {plotRightInset > 0 && <td style={{ width: `${plotRightInset}px`, minWidth: `${plotRightInset}px` }}></td>}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="text-center py-0.5 mt-4 text-[10px] font-bold" style={{ color: "#ffffff" }}>
              {bottomLabel}
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
    
  );
};

export default NewChartKPI;